/**
 * Centralized registry of every backend HTTP route consumed by the frontend.
 *
 * ## Why this file exists
 *
 * Per `frontend/AGENTS.md`, hardcoding `/api/...` strings inside components,
 * hooks, stores or utilities is forbidden. All API paths must be looked up
 * through this registry so that:
 *
 * - any backend route change is fixed in a single place,
 * - typos are caught at compile time (TypeScript knows every key),
 * - search / refactor across the project is trivial
 *   (`API_ROUTES.auth.login`, `API_ROUTES.blog.bySlug`).
 *
 * ## How to use
 *
 * ```ts
 * import { appFetch } from "@/Utils/Services/Fetch/appFetch";
 * import { API_ROUTES } from "@/config/apiRoutes";
 *
 * // Static route
 * const me = await appFetch(API_ROUTES.auth.me);
 * ```
 *
 * Groups expose static `string` paths and arrow-function builders for paths
 * with URL params (`API_ROUTES.group.byId(id)`).
 *
 * Blog content is served by the external CovalPress CMS, not this backend —
 * read it through the SDK client in `@/config/covalpress`, not `API_ROUTES`.
 *
 * ## How to add a new endpoint
 *
 * 1. Pick the right resource group below (or create a new one).
 * 2. Add a static `string` entry for fixed paths, or an arrow function
 *    returning a template literal for paths with URL parameters.
 * 3. Use the same parameter naming as the backend route (`id`, `token`, …).
 *    Builders accept `number | string` so consumers can pass either a
 *    numeric id or a stringified one.
 * 4. Never inline `/api/...` outside of this file.
 *
 * The whole tree is frozen with `as const satisfies ApiRouteTree` so each
 * leaf keeps its literal type and adding an entry that does not match the
 * `string | builder` shape is a type error.
 */

/**
 * Common prefix for every backend HTTP route.
 *
 * Defaults to the same-origin `"/api"`. Override it with `VITE_API_BASE_URL`
 * (see `.env.example`) to point the SPA at a cross-origin backend — auth cookies
 * still ride along because `appFetch` sends `credentials: "include"`. An empty
 * value falls back to `"/api"`, so leaving the env var blank is safe.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";

/**
 * Shape every leaf in {@link API_ROUTES} must respect:
 * either a static path string, or a function building a path from URL params.
 */
type ApiRouteLeaf = string | ((...args: (number | string)[]) => string);

/** Recursive shape of the route tree — groups of leaves or nested groups. */
interface ApiRouteTree {
  [key: string]: ApiRouteLeaf | ApiRouteTree;
}

/**
 * Single source of truth for every backend route called from the frontend.
 *
 * Groups mirror backend resource folders (`src/routes/<resource>.ts`).
 * Keys describe the operation (`list`, `byId`, `create`, `update`,
 * `delete`, …) so consumers read like English:
 * `API_ROUTES.blog.bySlug(slug)`.
 */
export const API_ROUTES = {
  /**
   * Authentication endpoints. The access + refresh tokens are delivered as
   * httpOnly cookies set by the backend (never in the response body), so the
   * browser sends them automatically with `credentials: "include"`.
   *
   * Backed by `backend/src/routes/auth.ts`.
   */
  auth: {
    /** Create a new user — sets the auth cookies, returns the user (`POST`). */
    register: `${API_BASE}/auth/register`,
    /** Exchange email + password — sets the auth cookies, returns the user (`POST`). */
    login: `${API_BASE}/auth/login`,
    /** Rotate the token pair from the refresh cookie, re-sets cookies (`POST`). */
    refresh: `${API_BASE}/auth/refresh`,
    /** Revoke the refresh token and clear the auth cookies (`POST`). */
    logout: `${API_BASE}/auth/logout`,
    /** Current user resolved from the access-token cookie (`GET`). */
    me: `${API_BASE}/auth/me`,
  },

  /**
   * Health-check endpoints exposed by the backend.
   *
   * Backed by `backend/src/routes/health.ts`.
   */
  health: {
    /** Liveness probe — must stay simple and stable. */
    live: `${API_BASE}/health/live`,
    /** Readiness probe — returns 503 when a critical dependency is down. */
    ready: `${API_BASE}/health/ready`,
    /** Detailed status: feature flags, strategies, mailer state, … */
    details: `${API_BASE}/health/details`,
  },
} as const satisfies ApiRouteTree;

/**
 * Compile-time type of the full route tree.
 * Useful when typing helpers that walk or extend the registry.
 */
export type ApiRoutes = typeof API_ROUTES;

/**
 * Union of the top-level resource group names (`"auth" | "health" | …`).
 * Handy for switch / lookup tables keyed by resource.
 */
export type ApiRouteGroup = keyof ApiRoutes;
